import type {
  Banner,
  BannerBlock,
  Block,
  CurrentFile,
  ID,
  Maybe,
  Response,
  StoreThis,
} from '@/types'
import type { ToastState } from '@/types/ui'

import { ref } from 'vue'
import { syncRef } from '@vueuse/core'

import { defineStore, storeToRefs } from 'pinia'
import { useModelsStore } from '@/stores/models'
import { useFilesStore } from '@/stores/files'
import { useUiStore } from '@/stores/ui'

import {
  clone,
  findByPropValue,
  mergeDeep,
  unProxy,
  handleErrors,
  isEmpty,
  findIndexByPropValue,
} from '@/helpers'

import BlockBannerModel from '@/models/BlockBannerModel'
import FileModel from '@/models/FileModel'
import Model from '@/models/Model'

type SingleType = Block
type ListItemType = Block

const resource = 'blocks'

export const useBlocksStore = defineStore('blocks', () => {
  const modelsStore = useModelsStore()
  const ui = useUiStore()

  const currentBlock = ref<any>({})
  const blocks = ref<ListItemType[]>([])

  const singleItemIsLoading = ref(false)

  const isLoading = ref(false)
  const isDeleting = ref(false)

  const filesStore = useFilesStore()
  const { isLoading: filesAreLoading, currentFiles } = storeToRefs(filesStore)

  syncRef(isLoading, filesAreLoading, { direction: 'rtl' })

  async function fetchItems(this: StoreThis, rolePrefix: 'm' | 'c' = 'm') {
    isLoading.value = true

    try {
      const {
        data: { data, success, errors },
      }: Response<Block[]> = await this.rest.get(`/${rolePrefix}/${resource}`)

      if (!success && errors) throw Error(handleErrors(errors))

      blocks.value = data ?? []
    } catch (e) {
      console.error('[fetchBlocks]', e)
    } finally {
      isLoading.value = false
    }
  }

  async function fetchBlockById(this: StoreThis, id: Maybe<ID>) {
    if (!Number.isInteger(id)) return

    singleItemIsLoading.value = true

    try {
      const {
        data: { data, success, errors },
      }: Response<SingleType> = await this.rest.get(`/m/${resource}/${id}`)

      if (!success && errors) throw Error(handleErrors(errors))

      const index = findIndexByPropValue(blocks.value, id)

      if (index !== -1) {
        Model.reset(blocks.value[index], data)
      } else blocks.value.push(data)
    } catch (e) {
      console.error('[fetchBlockById]', resource, e)
    } finally {
      singleItemIsLoading.value = false
    }
  }

  async function addBlock(
    this: StoreThis,
    payload = clone(unProxy(currentBlock))
  ) {
    const options: Partial<ToastState> = {}
    let id: Maybe<ID> = null
    let error = false
    let validationErrors: Record<keyof Block | string, string[]> = {}

    isLoading.value = true
    /**
     * Добавляем файлы, если баннеры
     * */
    if (/banner/.test(payload.type))
      prepareFiles(payload, currentFiles.value, FileModel)

    try {
      const {
        data: { success, errors, data },
      }: Response<Block> = await this.rest.post(`/m/${resource}`, payload)

      if (!success && errors) {
        if (Object.hasOwn(errors, 'validation')) {
          validationErrors = errors.validation ?? {}
        }
        throw Error(handleErrors(errors))
      }

      id = data.id

      options.type = 'success'
      options.text = 'Добавление прошло успешно'
    } catch (e) {
      error = true
      console.error('[addBlock]', e)

      options.text = 'Что-то пошло не так. Добавить не удалось'
    } finally {
      isLoading.value = false

      if (options.text) ui.showToast(options)
    }

    return { error, id, validationErrors }
  }

  async function editBlock(
    this: StoreThis,
    block = unProxy(currentBlock),
    blockId?: ID
  ) {
    const id: ID = block.id ?? blockId
    const options: Partial<ToastState> = {}
    let error = false
    let validationErrors: Record<keyof Block | string, string[]> = {}

    isLoading.value = true

    const payload = BlockBannerModel.prepare(block, [
      'id',
      'type_human',
      'sort',
    ])
    /**
     * Добавляем файлы, если баннеры
     * */
    if (/banner/.test(payload.type)) {
      prepareFiles(payload, currentFiles.value, FileModel)
    }

    try {
      const {
        data: { success, errors },
      }: Response<Block> = await this.rest.put(`/m/${resource}/${id}`, payload)

      if (!success && errors) {
        if (Object.hasOwn(errors, 'validation')) {
          validationErrors = errors.validation ?? {}
        }
        throw Error(handleErrors(errors))
      }

      options.type = 'success'
      options.text = 'Изменения сохранены'
    } catch (e) {
      error = true
      console.error('[editBlock]', e)

      options.text = 'Что-то пошло не так. Сохранить не удалось'
    } finally {
      isLoading.value = false

      if (options.text) ui.showToast(options)
    }

    return { error, id, validationErrors }
  }

  async function removeBlock(this: StoreThis, id: ID) {
    const options: Partial<ToastState> = {}
    let error = false
    isDeleting.value = true

    try {
      const {
        data: { success, errors },
      }: Response<any> = await this.rest.delete(`/m/${resource}/${id}`)

      if (!success && errors) throw Error(handleErrors(errors))
    } catch (e) {
      error = true
      console.error('[removeBlock]', e)

      options.text = 'Что-то пошло не так. Удалить не удалось'
    } finally {
      isDeleting.value = false

      if (options.text) ui.showToast(options)
    }

    return error
  }

  function getBlockById(id?: Maybe<ID>) {
    return Number.isInteger(id) ? findByPropValue(blocks.value, id) : null
  }

  function reset(snapshot?: Partial<Block>) {
    currentBlock.value = {}
    currentFiles.value = []

    if (snapshot) mergeDeep(currentBlock.value, snapshot)
  }

  return {
    currentBlock,
    blocks,

    isLoading,
    isDeleting,
    singleItemIsLoading,

    currentFiles,
    upload: filesStore.upload,

    fetch: fetchItems,
    fetchBlockById,

    addBlock,
    editBlock,
    removeBlock,

    toggle: modelsStore.toggle,
    sort: modelsStore.sort,

    getBlockById,
    reset,
  }
})

function prepareFiles(
  payload: Partial<BannerBlock>,
  files: CurrentFile[],
  Model: typeof FileModel
) {
  if (!files.length || !payload?.parameters?.banners) return

  payload.parameters.banners.forEach((banner: Banner, index: number) => {
    const currentFile = files[index]
    const added = currentFile.id !== null

    const maybeDeleted = isEmpty(
      Model.onlyModelKeysDiff(Model.get(), currentFile)
    )

    if (maybeDeleted && banner.image) banner.image = null
    else if (added) banner.image = currentFile.path
  })
}
